import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, Save, Trash2, Plus, Loader2, DollarSign, 
  Package, ToggleLeft, ToggleRight, Edit2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StaffProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: "",
    description: "",
    price_per_unit: "",
    unit: "lb",
    category: "Beef",
    available: true
  });

  useEffect(() => {
    const isLoggedIn = sessionStorage.getItem("staffLoggedIn");
    if (!isLoggedIn) {
      navigate("/staff");
    }
  }, [navigate]);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products/all`);
      setProducts(response.data);
    } catch (error) {
      console.error("Failed to fetch products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const updateProductPrice = async (productId, newPrice) => {
    setSaving(productId);
    try {
      const product = products.find(p => p.product_id === productId);
      await axios.put(`${API}/products/${productId}`, {
        ...product,
        price_per_unit: parseFloat(newPrice)
      });
      toast.success("Price updated");
      fetchProducts();
    } catch (error) {
      console.error("Failed to update price:", error);
      toast.error("Failed to update price");
    } finally {
      setSaving(null);
    }
  };

  const toggleProductAvailability = async (productId, currentStatus) => {
    setSaving(productId);
    try {
      const product = products.find(p => p.product_id === productId);
      await axios.put(`${API}/products/${productId}`, {
        ...product,
        available: !currentStatus
      });
      toast.success(currentStatus ? "Item discontinued" : "Item now available");
      fetchProducts();
    } catch (error) {
      console.error("Failed to update availability:", error);
      toast.error("Failed to update item");
    } finally {
      setSaving(null);
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    
    setSaving(productId);
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success("Product deleted");
      fetchProducts();
    } catch (error) {
      console.error("Failed to delete product:", error);
      toast.error("Failed to delete product");
    } finally {
      setSaving(null);
    }
  };

  const addProduct = async () => {
    if (!newProduct.name || !newProduct.price_per_unit) {
      toast.error("Please fill in name and price");
      return;
    }
    
    setSaving("new");
    try {
      await axios.post(`${API}/products`, {
        ...newProduct,
        price_per_unit: parseFloat(newProduct.price_per_unit),
        image_url: ""
      });
      toast.success("Product added");
      setShowAddDialog(false);
      setNewProduct({
        name: "",
        description: "",
        price_per_unit: "",
        unit: "lb",
        category: "Beef",
        available: true
      });
      fetchProducts();
    } catch (error) {
      console.error("Failed to add product:", error);
      toast.error("Failed to add product");
    } finally {
      setSaving(null);
    }
  };

  const groupedProducts = products.reduce((acc, product) => {
    if (!acc[product.category]) acc[product.category] = [];
    acc[product.category].push(product);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-black" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="staff-products">
      {/* Header */}
      <header className="bg-black text-white sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/staff/dashboard" className="flex items-center gap-2 text-white hover:text-gray-300">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Orders</span>
          </Link>
          <h1 className="text-lg font-[800]" style={{ fontFamily: 'Fraunces, serif' }}>
            Manage Products
          </h1>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-[#1A5E1F] hover:bg-[#155219]">
                <Plus className="w-4 h-4 mr-1" />
                Add Item
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Product</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label>Product Name *</Label>
                  <Input
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                    placeholder="e.g., T-Bone Steak"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    value={newProduct.description}
                    onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                    placeholder="e.g., Premium Zabiha Halal cut"
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Price per lb *</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="number"
                        step="0.01"
                        value={newProduct.price_per_unit}
                        onChange={(e) => setNewProduct({...newProduct, price_per_unit: e.target.value})}
                        placeholder="0.00"
                        className="pl-8"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={newProduct.category} onValueChange={(v) => setNewProduct({...newProduct, category: v})}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Beef">Beef</SelectItem>
                        <SelectItem value="Poultry">Poultry</SelectItem>
                        <SelectItem value="Lamb">Lamb</SelectItem>
                        <SelectItem value="Goat">Goat</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button
                  onClick={addProduct}
                  disabled={saving === "new"}
                  className="w-full bg-[#1A5E1F] hover:bg-[#155219]"
                >
                  {saving === "new" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Add Product
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {Object.entries(groupedProducts).map(([category, categoryProducts]) => (
          <div key={category} className="bg-white border-2 border-[#DCD7CC] overflow-hidden">
            <div className="bg-[#1A1A1A] text-white px-4 py-3 flex justify-between items-center">
              <h2 className="text-lg font-[700]" style={{ fontFamily: 'Fraunces, serif' }}>{category}</h2>
              <span className="text-sm text-gray-400">{categoryProducts.length} items</span>
            </div>
            <div className="divide-y divide-[#DCD7CC]">
              {categoryProducts.map(product => (
                <div 
                  key={product.product_id}
                  className={`p-4 ${!product.available ? 'bg-gray-100 opacity-60' : ''}`}
                  data-testid={`product-row-${product.product_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-[600] text-[#1A1A1A]">{product.name}</h3>
                        {!product.available && (
                          <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">Discontinued</span>
                        )}
                      </div>
                      <p className="text-sm text-[#8A8A8A]">{product.description}</p>
                    </div>
                    
                    {/* Price Editor */}
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="number"
                          step="0.01"
                          defaultValue={product.price_per_unit}
                          onBlur={(e) => {
                            if (parseFloat(e.target.value) !== product.price_per_unit) {
                              updateProductPrice(product.product_id, e.target.value);
                            }
                          }}
                          className="w-24 pl-7 text-right font-bold"
                          data-testid={`price-${product.product_id}`}
                        />
                      </div>
                      <span className="text-sm text-[#8A8A8A]">/lb</span>
                    </div>

                    {/* Availability Toggle */}
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`avail-${product.product_id}`} className="text-xs text-[#8A8A8A]">
                        {product.available ? 'Available' : 'Discontinued'}
                      </Label>
                      <Switch
                        id={`avail-${product.product_id}`}
                        checked={product.available}
                        onCheckedChange={() => toggleProductAvailability(product.product_id, product.available)}
                        disabled={saving === product.product_id}
                        data-testid={`toggle-${product.product_id}`}
                      />
                    </div>

                    {/* Delete */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteProduct(product.product_id)}
                      disabled={saving === product.product_id}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-${product.product_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StaffProducts;
